import { useEffect, useRef, useState } from 'react';
import {
  ProspectListColumnTarget,
  type ProspectListBinding,
  type ProspectListNameOrder,
  type ProspectListPreviewResponse,
} from '@signalvote/shared';
import { Upload } from 'lucide-react';
import { ColumnMappingGrid } from '@/components/finance/import';
import { useConfirmProspectList, useDetectProspectList, usePreviewProspectList } from '@/hooks/queries';
import { assignmentsToBindings, bindingsToAssignments } from '@/lib/fundraising/prospectListUpload';
import { cn } from '@/lib/utils';
import { ProspectListGate } from './ProspectListGate';
import { ProspectListTemplateGuide } from './ProspectListTemplateGuide';

/**
 * The mapping grid's choices, keyed by the backend target name. "Don't import" is the grid's own skip
 * option, so it is not listed here.
 */
const TARGET_OPTIONS: { value: ProspectListColumnTarget; label: string }[] = [
  { value: ProspectListColumnTarget.FullName, label: 'Full name' },
  { value: ProspectListColumnTarget.FirstName, label: 'First name' },
  { value: ProspectListColumnTarget.LastName, label: 'Last name' },
  { value: ProspectListColumnTarget.Phone, label: 'Phone' },
  { value: ProspectListColumnTarget.Email, label: 'Email' },
  { value: ProspectListColumnTarget.Street, label: 'Street address' },
  { value: ProspectListColumnTarget.AddressLine2, label: 'Address line 2' },
  { value: ProspectListColumnTarget.City, label: 'City' },
  { value: ProspectListColumnTarget.State, label: 'State' },
  { value: ProspectListColumnTarget.Zip, label: 'ZIP code' },
  { value: ProspectListColumnTarget.Employer, label: 'Employer' },
  { value: ProspectListColumnTarget.Occupation, label: 'Occupation' },
  { value: ProspectListColumnTarget.Notes, label: 'Notes' },
];

const bound = (bindings: ProspectListBinding[], ...targets: ProspectListColumnTarget[]) =>
  bindings.some((b) => targets.includes(b.target));

/**
 * The columns need a hand choice when what every row needs wasn't found: a name (whole, or first and
 * last) and a ZIP code or state. A column it recognised as nothing, like a Remarks column, is left out
 * quietly and doesn't count.
 */
function needsAHand(bindings: ProspectListBinding[]): boolean {
  const T = ProspectListColumnTarget;
  const name = bound(bindings, T.FullName) || (bound(bindings, T.FirstName) && bound(bindings, T.LastName));
  return !name || !bound(bindings, T.Zip, T.State);
}

/**
 * The overlay's first step, Your file: choose or drop a CSV and the whole-file check runs straight away,
 * and again whenever the name order or a column changes. The column mapping shows only when the
 * columns need a hand choice (`onWideChange` widens the overlay for it); otherwise it waits behind
 * "Change columns". Nothing is written until the one button, which hands the new upload's id to `onStarted`.
 */
export function AddPeopleFileStep({
  onStarted,
  onWideChange,
  initialFile = null,
}: {
  onStarted: (prospectListId: string) => void;
  onWideChange: (wide: boolean) => void;
  /** A file already chosen before the step opened (dropped on the empty book); checked straight away. */
  initialFile?: File | null;
}) {
  const { mutate: detect, data: detection, isPending: isDetecting, error: detectError } = useDetectProspectList();
  const { mutate: preview, isPending: isChecking, error: previewError } = usePreviewProspectList();
  const { mutate: confirm, isPending: isConfirming, error: confirmError } = useConfirmProspectList();
  // The last check that came back. Kept while a re-check is out, so the counts don't blink away; the
  // button waits for the re-check (`isChecking`), since these counts are for the choices before it.
  const [previewed, setPreviewed] = useState<ProspectListPreviewResponse | null>(null);
  const [bindings, setBindings] = useState<ProspectListBinding[]>([]);
  const [nameOrder, setNameOrder] = useState<ProspectListNameOrder | undefined>(undefined);
  const [listName, setListName] = useState('');
  const [showColumns, setShowColumns] = useState(false);
  const [dragging, setDragging] = useState(false);

  const check = (objectKey: string, next: ProspectListBinding[], order: ProspectListNameOrder | null) =>
    preview({ objectKey, bindings: next, nameOrder: order }, { onSuccess: setPreviewed });

  const onFile = (file: File | undefined) => {
    if (!file) return;
    // The last file's counts go with it, so the button can't add the wrong file.
    setPreviewed(null);
    detect(file, {
      onSuccess: (d) => {
        const suggested = d.suggestedBindings.map(({ sourceColumn, target }) => ({ sourceColumn, target }));
        const unrecognised = needsAHand(suggested);
        setBindings(suggested);
        setNameOrder(undefined);
        setListName(d.fileName.replace(/\.csv$/i, ''));
        setShowColumns(unrecognised);
        onWideChange(unrecognised);
        check(d.objectKey, suggested, null);
      },
    });
  };

  // Once per file handed in, however often the step re-renders.
  const takenFile = useRef<File | null>(null);
  useEffect(() => {
    if (!initialFile || takenFile.current === initialFile) return;
    takenFile.current = initialFile;
    onFile(initialFile);
    // onFile is recreated each render; the file is what decides whether to run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFile]);

  const changeBindings = (next: ProspectListBinding[]) => {
    if (!detection) return;
    setBindings(next);
    check(detection.objectKey, next, nameOrder ?? null);
  };

  const changeNameOrder = (order: ProspectListNameOrder) => {
    if (!detection) return;
    setNameOrder(order);
    check(detection.objectKey, bindings, order);
  };

  const onConfirm = () => {
    if (!detection || !previewed) return;
    confirm(
      {
        objectKey: detection.objectKey,
        bindings,
        // The order the person saw the names split in is the order the upload splits them in.
        nameOrder: previewed.nameOrder,
        name: listName,
        fileName: detection.fileName,
      },
      { onSuccess: (r) => onStarted(r.prospectListId) },
    );
  };

  return (
    <div className="space-y-4 p-5 text-sm">
      <div className="pr-8">
        <h2 className="text-lg font-semibold text-foreground">Your file</h2>
        <p className="text-muted-foreground">
          A CSV of the people you want to call. Each row needs a name and a ZIP code or state; phone and email are
          optional.
        </p>
      </div>
      <ProspectListTemplateGuide />

      <label
        className={cn(
          'flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-dashed px-4 py-5 text-center transition-colors',
          dragging ? 'border-foreground bg-muted' : 'hover:bg-muted/60',
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          onFile(e.dataTransfer.files?.[0]);
        }}
      >
        <Upload className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        <span className="font-medium text-foreground">
          {isDetecting ? 'Reading the file…' : detection ? detection.fileName : 'Choose a CSV file'}
        </span>
        <span className="text-xs text-muted-foreground">
          {detection ? 'Choose another file to replace it' : 'or drop it here'}
        </span>
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label="Choose a CSV file"
          className="sr-only"
          disabled={isDetecting}
          onChange={(e) => {
            onFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </label>
      {detectError && <p className="text-amber-700">{detectError.message}</p>}

      {detection &&
        (showColumns ? (
          <ColumnMappingGrid
            headers={detection.detectedColumns}
            targets={TARGET_OPTIONS}
            assignments={bindingsToAssignments(detection.detectedColumns, bindings)}
            onChange={(assignments) => changeBindings(assignmentsToBindings(detection.detectedColumns, assignments))}
          />
        ) : (
          <button
            type="button"
            className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
            onClick={() => {
              setShowColumns(true);
              onWideChange(true);
            }}
          >
            Change columns
          </button>
        ))}

      {!previewed && isChecking && (
        <p role="status" className="text-muted-foreground">
          Checking every row…
        </p>
      )}
      {previewError && <p className="text-amber-700">{previewError.message}</p>}

      {previewed && (
        <ProspectListGate
          preview={previewed}
          listName={listName}
          onListNameChange={setListName}
          onNameOrderChange={changeNameOrder}
          onConfirm={onConfirm}
          isConfirming={isConfirming}
          checking={isChecking || !!previewError}
          confirmError={confirmError?.message}
        />
      )}
    </div>
  );
}
